const animation = [
  "carousel",
  "carousel-inverse",
  "swing",
  "elastic"
] as const

const color = [
  "primary",
  "secondary",
  "success",
  "info",
  "warning",
  "error",
  "neutral"
] as const

const size = [
  "2xs",
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "2xl"
] as const

const step = [
  "active",
  "first",
  "other",
  "last"
] as const

const orientation = [
  "horizontal",
  "vertical"
] as const

export default {
  "slots": {
    "root": "tw:gap-2",
    "base": "tw:relative tw:overflow-hidden tw:rounded-full tw:bg-accented",
    "indicator": "tw:rounded-full tw:size-full tw:transition-transform tw:duration-200 tw:ease-out tw:motion-reduce:transition-none tw:motion-reduce:data-[state=indeterminate]:animate-pulse",
    "status": "tw:flex tw:text-dimmed tw:duration-200 tw:ease-out tw:motion-reduce:transition-none",
    "steps": "tw:grid tw:items-end",
    "step": "tw:truncate tw:text-end tw:row-start-1 tw:col-start-1 tw:transition-opacity tw:ease-out" as typeof step[number]
  },
  "variants": {
    "animation": {
      "carousel": "",
      "carousel-inverse": "",
      "swing": "",
      "elastic": ""
    },
    "color": {
      "primary": {
        "indicator": "tw:bg-primary",
        "steps": "tw:text-primary"
      },
      "secondary": {
        "indicator": "tw:bg-secondary",
        "steps": "tw:text-secondary"
      },
      "success": {
        "indicator": "tw:bg-success",
        "steps": "tw:text-success"
      },
      "info": {
        "indicator": "tw:bg-info",
        "steps": "tw:text-info"
      },
      "warning": {
        "indicator": "tw:bg-warning",
        "steps": "tw:text-warning"
      },
      "error": {
        "indicator": "tw:bg-error",
        "steps": "tw:text-error"
      },
      "neutral": {
        "indicator": "tw:bg-inverted",
        "steps": "tw:text-highlighted"
      }
    },
    "size": {
      "2xs": {
        "status": "tw:text-xs",
        "steps": "tw:text-xs"
      },
      "xs": {
        "status": "tw:text-xs",
        "steps": "tw:text-xs"
      },
      "sm": {
        "status": "tw:text-sm",
        "steps": "tw:text-sm"
      },
      "md": {
        "status": "tw:text-sm",
        "steps": "tw:text-sm"
      },
      "lg": {
        "status": "tw:text-sm",
        "steps": "tw:text-sm"
      },
      "xl": {
        "status": "tw:text-base",
        "steps": "tw:text-base"
      },
      "2xl": {
        "status": "tw:text-base",
        "steps": "tw:text-base"
      }
    },
    "step": {
      "active": {
        "step": "tw:opacity-100" as typeof step[number]
      },
      "first": {
        "step": "tw:opacity-100 tw:text-muted" as typeof step[number]
      },
      "other": {
        "step": "tw:opacity-0" as typeof step[number]
      },
      "last": {
        "step": ""
      }
    },
    "orientation": {
      "horizontal": {
        "root": "tw:w-full tw:flex tw:flex-col",
        "base": "tw:w-full",
        "status": "tw:flex-row tw:items-center tw:justify-end tw:w-(--percent) tw:min-w-fit tw:transition-[width]"
      },
      "vertical": {
        "root": "tw:h-full tw:flex tw:flex-row-reverse",
        "base": "tw:h-full",
        "status": "tw:flex-col tw:justify-end tw:h-(--percent) tw:min-h-fit tw:transition-[height]"
      }
    },
    "inverted": {
      "true": {
        "status": "tw:self-end"
      }
    }
  },
  "compoundVariants": [
    {
      "inverted": true,
      "orientation": "horizontal" as typeof orientation[number],
      "class": {
        "step": "tw:text-start" as typeof step[number],
        "status": "tw:flex-row-reverse"
      }
    },
    {
      "inverted": true,
      "orientation": "vertical" as typeof orientation[number],
      "class": {
        "steps": "tw:items-start",
        "status": "tw:flex-col-reverse"
      }
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "2xs" as typeof size[number],
      "class": "tw:h-px"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "xs" as typeof size[number],
      "class": "tw:h-0.5"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "sm" as typeof size[number],
      "class": "tw:h-1"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "md" as typeof size[number],
      "class": "tw:h-2"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "lg" as typeof size[number],
      "class": "tw:h-3"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "xl" as typeof size[number],
      "class": "tw:h-4"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "size": "2xl" as typeof size[number],
      "class": "tw:h-5"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "2xs" as typeof size[number],
      "class": "tw:w-px"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "xs" as typeof size[number],
      "class": "tw:w-0.5"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "sm" as typeof size[number],
      "class": "tw:w-1"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "md" as typeof size[number],
      "class": "tw:w-2"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "lg" as typeof size[number],
      "class": "tw:w-3"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "xl" as typeof size[number],
      "class": "tw:w-4"
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "size": "2xl" as typeof size[number],
      "class": "tw:w-5"
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "animation": "carousel" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[carousel_2s_linear_infinite] tw:motion-safe:data-[state=indeterminate]:rtl:animate-[carousel-rtl_2s_linear_infinite]"
      }
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "animation": "carousel" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[carousel-vertical_2s_linear_infinite]"
      }
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "animation": "carousel-inverse" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[carousel-inverse_2s_linear_infinite] tw:motion-safe:data-[state=indeterminate]:rtl:animate-[carousel-inverse-rtl_2s_linear_infinite]"
      }
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "animation": "carousel-inverse" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[carousel-inverse-vertical_2s_linear_infinite]"
      }
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "animation": "swing" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[swing_2s_var(--ease-in-out)_infinite]"
      }
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "animation": "swing" as typeof animation[number],
      "class": {
        "indicator": "tw:motion-safe:data-[state=indeterminate]:animate-[swing-vertical_2s_var(--ease-in-out)_infinite]"
      }
    },
    {
      "orientation": "horizontal" as typeof orientation[number],
      "animation": "elastic" as typeof animation[number],
      "class": {
        "indicator": "tw:relative tw:motion-safe:data-[state=indeterminate]:animate-[elastic_2s_var(--ease-in-out)_infinite]"
      }
    },
    {
      "orientation": "vertical" as typeof orientation[number],
      "animation": "elastic" as typeof animation[number],
      "class": {
        "indicator": "tw:relative tw:motion-safe:data-[state=indeterminate]:animate-[elastic-vertical_2s_var(--ease-in-out)_infinite]"
      }
    }
  ],
  "defaultVariants": {
    "animation": "carousel" as typeof animation[number],
    "color": "primary" as typeof color[number],
    "size": "md" as typeof size[number]
  }
}